import React from 'react';
import {useTheme} from '@unthinkable/react-theme';
import CountCellRender from '../../../app-components/renders/CountCellRender';
import {PrimaryColumnCard} from '../../../components/card/PrimaryColumnCard';
import {
  GroupFilter,
  PeriodRangeFilter,
} from '../../../components/filter/Filters';
import {Table} from '../../../components/table/Table';
import {useFilter} from '../../../controllers/useFilter';
import {useAppStateContext} from '../../../providers/AppState';
import {getPreviousMonth} from '@unthinkable/react-filters';
import {Row, Text} from '@unthinkable/react-core-components';
import {TabView} from '../../../components/tab';
import {TableHeader} from '../../../components/table/Headers';
import {IconButton} from '../../../components/button/IconButton';

const projectOnpress = ({navigation, row}) => {
  return navigation.navigate(`project-home`, {
    _id: row?.project,
    project: row?.project,
  });
};

const customerOnpress = ({navigation, row, params}) => {
  return navigation.navigate(`customer-details`, {
    ...params,
    customerId: row?.customer?._id,
    row: {
      _id: row?.customer?._id,
      name: row?.customer?.name,
    },
  });
};

export const ProjectGrossMarginTable = props => {
  let {
    navigation,
    route: {params},
    filterParams,
    filter,
    isCustomerGrossMargin = false,
  } = props;
  filterParams = {...filterParams, ...params};
  return (
    <Table
      {...props}
      params={{...filterParams, isCustomerGrossMargin}}
      addOnFilter={{...filter, ...params?.customerFilter}}
      api={'/projects/teamcostreport'}
      limit={5000}
      renderHeader={() =>
        params?.fromCustomer && (
          <TableHeader title={'T&M Project Gross Margin'} />
        )
      }
      columns={[
        {
          header: isCustomerGrossMargin ? 'Customer' : 'Project',
          minWidth: 200,
          render: ({row}) => {
            let type = '';
            if (row.projecttype === 'tm') {
              type = 'T&M';
            } else if (row.projecttype === 'i') {
              type = 'Internal';
            } else if (row.projecttype === 'f') {
              type = 'Fixed';
            }
            return (
              <PrimaryColumnCard
                primaryTitle={
                  isCustomerGrossMargin
                    ? row?.customer?.name
                    : row?.project?.project
                }
                items={[
                  {
                    value: row?.code,
                  },
                  {
                    value: type,
                  },
                ]}
              />
            );
          },
          onPress: ({row}) =>
            isCustomerGrossMargin
              ? customerOnpress({navigation, row, params})
              : projectOnpress({navigation, row}),
        },
        {
          header: 'AM',
          type: 'userAvatar',
          align: 'center',
          field: 'customer.owner',
          width: 60,
        },
        {
          header: 'CSM',
          type: 'userAvatar',
          align: 'center',
          field: 'customer.csm',
          width: 60,
        },
        isCustomerGrossMargin && {
          render: CountCellRender,
          header: 'Projects',
          align: 'right',
          count_field: 'projectCounts',
          width: 80,
          onPress: ({row}) => {
            navigation.navigate('project-gross-margin-report', {
              ...params,
              fromCustomer: true,
              customerFilter: {customer: row?.customer?._id},
            });
          },
        },
        {
          render: CountCellRender,
          header: 'Team',
          align: 'right',
          width: 80,
          count_field: 'project_team',
          onPress: ({row}) => {
            navigation.navigate('eng_gross_margin_report', {
              ...row,
              employeeId: row.employeeId,
              project_id: !isCustomerGrossMargin && row?.project?._id,
              customer: isCustomerGrossMargin && row?.customer?._id,
              api: '/employee/engineeringReport',
              period: filterParams.period,
            });
          },
        },
        {
          header: 'Avg Exp',
          field: 'avg_experience',
          align: 'right',
          width: 80,
          render: ({row, styles}) => {
            return (
              <Text style={{...styles.rowText, color: '#737373'}}>
                {row.avg_experience} y
              </Text>
            );
          },
        },
        {
          aggregate: true,
          count_field: 'revenue',
          header: 'Revenue',
          type: 'currency',
          field: 'revenue',
          align: 'right',
          width: 120,
        },
        {
          aggregate: true,
          count_field: 'cost',
          header: 'Cost',
          type: 'currency',
          field: 'cost',
          align: 'right',
          width: 120,
        },
        {
          aggregate: true,
          count_field: 'gross_margin',
          header: 'Gross Margin',
          type: 'currency',
          field: 'gross_margin',
          align: 'right',
          width: 120,
        },
        {
          count_field: 'avg_profit_per_person',
          header: 'Gross Margin %',
          field: 'avg_profit_per_person',
          align: 'right',
          width: 140,
          render: ({row, styles}) => {
            return (
              <Row
                style={{
                  backgroundColor: row.avgColor,
                  width: 120,
                  flex: 1,
                  alignItems: 'center',
                  overflow: 'hidden',
                  justifyContent: 'center',
                  borderRadius: 8,
                }}>
                <Text style={{...styles.rowText}}>
                  {row.avg_gross_margin_percentage
                    ? `${Number(row?.avg_gross_margin_percentage).toFixed(1)}%`
                    : '0'}
                </Text>
              </Row>
            );
          },
        },
      ]}
    />
  );
};

export const CustomerGrossMarginTab = props => {
  let {route: {params} = {}} = props;
  const {applyFilter, filterValues, onChangeFilter} = useFilter({
    params: {
      period: getPreviousMonth(),
    },
  });
  const icons = useTheme('icons');
  const {params: filterParams, filter} = filterValues;
  const {period} = filterParams;
  const {download} = useAppStateContext();
  const tabs = {
    cutomer_gross_margin_report: {
      label: 'T&M Customer',
      view: (
        <ProjectGrossMarginTable
          key="CustomerGrossMargin"
          filterParams={filterParams}
          filter={{...filter, period}}
          isCustomerGrossMargin={true}
        />
      ),
      actions: [
        <PeriodRangeFilter
          onChangeFilter={onChangeFilter}
          filterValues={filterValues}
        />,
        <GroupFilter
          filterValues={filterValues}
          applyFilter={applyFilter}
          filters={[
            {
              type: 'autoComplete',
              label: 'Customer',
              api: '/customers',
              placeholder: 'Select',
              suggestionField: 'name',
              valueField: 'name',
              field: 'customer',
            },
            {
              type: 'autoComplete',
              field: 'project_id',
              api: '/projects',
              suggestionField: 'project',
              valueField: 'project',
              label: 'Project',
            },
          ]}
        />,
        <IconButton
          icon={icons.Download}
          onPress={() => {
            download({
              uri: '/exportTandMProjectGrossMarginReport',
              props: {
                uri: '/projects/teamcostreport',
                parameters: {
                  params: {...filterParams, isCustomerGrossMargin: true},
                  addOnFilter: {...filter, ...params?.customerFilter},
                },
              },
            });
          }}
        />,
      ],
    },
  };
  return <TabView tabs={tabs} params={params} {...props} />;
};
